import React, { useRef, useEffect, useCallback, useState} from 'react';
import {
    PADS_SLOT_EMPTY,
    PADS_SLOT
} from "constants/contextual-help.js";
import {PIANO_ROLL, WAVEFORM} from 'constants/arrangement-modes.js';
import {useContextMenu} from "components/dropdowns/useContextMenu.js";
import ContextMenu from "components/dropdowns/ContextMenu.js";
import MenuItem from "components/dropdowns/MenuItem.js";
import {useSlotDrop} from './useSlotDrop.js';
import Colors from 'components/Colors.js';
import {useVolumeMove} from "components/explore/details/useVolumeMove.js";
import TrackThumbnail from "components/TrackThumbnail.js";
import {connect} from 'react-redux';
import {
    setArrangementBottomMode,
    setShowPianoRoll,
    setContextualHelp,
    setParameterSliderDown,
    setSampleTagDragging,
    setTrackDragging,
    setSampleDragging
} from 'actions/index.js';
import {DELETE,MUTES} from './modes.js';

const colors = new Colors();
const DOUBLE_CLICK = 300;

const PadSlot = ({
    muted,
    trackToShow,
    setContextualHelp,
    setTrackToShow,
    playing,
    setParameterSliderDown,
    notesTriggered,
    slotNumber,
    slot,
    pad,
    sampleDragging,
    setSampleDragging,
    isSelected,
    sampleTagDragging,
    setSampleTagDragging,
    mode,
    setArrangementBottomMode,
    setShowPianoRoll,
    isEnabled,
    setTrackDragging,
    trackDragging
}) => { 
    const ref = useRef(undefined);

    const {onContextMenu, setDropdownPos, dropdownPos} = useContextMenu(
        {ref});

    const {
        innerStyle,
        onMouseDown,
        onMouseMove,
        onMouseLeave,
        mouseDown,
        onMouseUp} = useVolumeMove(ref, slot, setParameterSliderDown, mode || dropdownPos);

    const {onDrop} = useSlotDrop({
        sampleDragging,
        slotNumber,
        pad,
        sampleTagDragging,
        setSampleDragging,
        setSampleTagDragging,
        slot,
        trackDragging,
        setTrackDragging
    });

    const _onMouseDown = useCallback((e) => {
        if (slot) {
            pad.sequencerEngine.selectTrack(
                slot.trackNumber);
            onMouseDown(e);

            if (mode === DELETE) {
                slot.deleting = true;
            } else if (mode === MUTES) {
                slot.mute(!muted);
            }
        }
    }, [mode, slot, onMouseDown, trackToShow, setTrackToShow, muted]);

    const _onMouseUp = useCallback((e) => {
        if (slot) {
            pad.sequencerEngine.selectTrack(
                slot.trackNumber);
            onMouseUp(e);
            if (mode === DELETE) {
                slot.deleting = false;
            }
        }
    }, [mode, slot, onMouseUp]);

    const selectTrack = useCallback((e) => {
        e.stopPropagation();
        if (slot) {
            pad.sequencerEngine.selectTrack(
                slot.trackNumber);
        }
    }, [slot, pad]);

    const toggleGated = useCallback((e) => {
        if (slot) {
            slot.gated = !slot.gated;
        }
    }, [slot]);

    const showWaveform = useCallback((e) => {
        setShowPianoRoll(true);
        setArrangementBottomMode(WAVEFORM);
    }, []);

    const toggleRecordingEnabled = useCallback((e) => {
        e.stopPropagation();
        slot.toggleEnabled();
    }, [isEnabled, slot]);

    let className = "pad-slot";
    if (!slot) {
        className += " empty";
    }
    if (isSelected) {
        className += " selected";
    }

    if (playing) {
        className += " pressed";
    }

    if (mode === DELETE && mouseDown) {
        className += " deleting";
    }

    if (muted) {
        className += " muted";
    }

    let keyMapping = undefined;
    if (slot) {
        keyMapping = slot.getKeyMapping();
    }

    return (
        <div
          ref={ref}
          onContextMenu={onContextMenu}
          onMouseMove={onMouseMove}
          onMouseOver={() => setContextualHelp(
              slot ? PADS_SLOT : PADS_SLOT_EMPTY)}
          onTouchStart={onMouseDown}
          onMouseUp={_onMouseUp}
          onMouseLeave={(e) => {
              setContextualHelp(undefined);
              onMouseLeave(e);
          }}
          onDrop={onDrop}
          onMouseDown={slot ? _onMouseDown : () => []}
          className={className}>
          {slot ? <TrackThumbnail
                    onMouseDown={(e) => {
                        if (!e.metaKey && !muted) {
                            e.stopPropagation();
                        }
                    }}
                    showPresetName={true} track={slot}/> : ''}
          <div className={mouseDown ? "volume-indicator"  : "volume-indicator skinny"} style={innerStyle}/>
          {slot &&
           <div
             onMouseDown={toggleRecordingEnabled}
             className={isEnabled ? "recording-dot enabled" : "recording-dot"}
           /> }
          {keyMapping &&
           <div className="pad-key-map">{keyMapping.toUpperCase()}</div>}
          <ContextMenu dropdownPos={dropdownPos}>
            {keyMapping &&
             <div className="mapped-to-key">Mapped to <span className="key-map">{keyMapping.toUpperCase()}</span></div>}
            <MenuItem onMouseDown={toggleGated}>
              {slot && slot.gated ? "Gated" : "Ungated"}
            </MenuItem>
            <MenuItem onMouseDown={showWaveform}>
              Show Waveform
            </MenuItem>
          </ContextMenu>
        </div>
    );
}

const mapStateToProps = (state, props) => {
    let ret = {
        sampleDragging: state.selector.sampleDragging,
        sampleTagDragging: state.selector.sampleTagDragging,
        isSelected: props.slot && state.mix.selectedTrack === props.slot.trackNumber,
        trackDragging: state.tracks.trackDragging,
    };
    let slot = props.slot;
    if (slot) {
        let trackTriggers = state.tracks.triggers[slot.trackNumber];
        ret.playing = (trackTriggers && Object.keys(trackTriggers).some(
            t => trackTriggers[t])) ||
            (props.slot.notesTriggered && Object.keys(props.slot.notesTriggered).length > 0);
        ret.notesTriggered = props.slot.notesTriggered;
    }
    if (slot) {
        ret.isEnabled = slot.isEnabled;
        ret.muted = state.tracks.trackMutes[slot.id]; 
    }
    return ret;
};

const mapDispatchToProps = (dispatch) => {
  return {
      setSampleDragging : (sample) => dispatch(setSampleDragging(sample)),
      setTrackDragging : (track) => dispatch(setTrackDragging(track)),
      setSampleTagDragging : (sample) => dispatch(setSampleTagDragging(sample)),
      setParameterSliderDown: parameter => dispatch(setParameterSliderDown(parameter)),
      setArrangementBottomMode: x => dispatch(setArrangementBottomMode(x)),
      setShowPianoRoll: x => dispatch(setShowPianoRoll(x)),
      setContextualHelp: x => dispatch(setContextualHelp(x))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PadSlot);
